import { useState, useEffect } from 'react';
import { Shield, Plus, Edit2, ToggleLeft, ToggleRight, X, Phone, Mail, Globe, MapPin, Building2 } from 'lucide-react';
import API_URL from '../config/api';

const InsurerManager = () => {
    const [insurers, setInsurers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingInsurer, setEditingInsurer] = useState(null);
    const [formData, setFormData] = useState({
        name: ''
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchInsurers();
    }, []);

    const fetchInsurers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/insurers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setInsurers(data);
            }
        } catch (error) {
            console.error('Error fetching insurers:', error);
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setEditingInsurer(null);
        setFormData({
            name: ''
        });
        setShowModal(true);
    };

    const openEditModal = (insurer) => {
        setEditingInsurer(insurer);
        setFormData({
            name: insurer.name || ''
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name) return;

        try {
            setSubmitting(true);
            const token = localStorage.getItem('token');
            const url = editingInsurer
                ? `${API_URL}/insurers/${editingInsurer.id}`
                : `${API_URL}/insurers`;
            const method = editingInsurer ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                setShowModal(false);
                fetchInsurers();
            } else {
                const error = await res.json();
                alert(error.message || 'Error al guardar');
            }
        } catch (error) {
            alert('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActive = async (insurer) => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/insurers/${insurer.id}/toggle`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                fetchInsurers();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
        }
    };

    return (
        <div className="insurer-manager">
            <div className="manager-header">
                <div className="header-info">
                    <Shield size={24} />
                    <div>
                        <h3>Aseguradoras</h3>
                        <p>Gestión de compañías de seguros y convenios</p>
                    </div>
                </div>
                <button className="btn-primary" onClick={openAddModal}>
                    <Plus size={18} />
                    Nueva Aseguradora
                </button>
            </div>

            <div className="insurers-grid">
                {loading ? (
                    <div className="loading">Cargando...</div>
                ) : insurers.length === 0 ? (
                    <div className="empty-state">
                        <Shield size={48} />
                        <p>No hay aseguradoras registradas</p>
                        <button onClick={openAddModal}>Agregar la primera</button>
                    </div>
                ) : (
                    insurers.map(insurer => (
                        <div key={insurer.id} className={`insurer-card ${insurer.is_active ? '' : 'inactive'}`}>
                            <div className="insurer-header">
                                <div className="insurer-name">
                                    <Shield size={20} />
                                    <span>{insurer.name}</span>
                                </div>
                            </div>



                            <div className="insurer-actions">
                                <button
                                    className="btn-icon"
                                    title="Editar"
                                    onClick={() => openEditModal(insurer)}
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    className="btn-icon toggle"
                                    title={insurer.is_active ? 'Desactivar' : 'Activar'}
                                    onClick={() => toggleActive(insurer)}
                                >
                                    {insurer.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content insurer-form-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingInsurer ? 'Editar Aseguradora' : 'Nueva Aseguradora'}</h2>
                            <button className="close-btn" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label>Nombre de la Aseguradora *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Ej: GNP Seguros"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit" disabled={submitting}>
                                    {submitting ? 'Guardando...' : (editingInsurer ? 'Guardar Cambios' : 'Crear Aseguradora')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .insurer-manager {
                    padding: 1rem 0;
                }
                .manager-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }
                .header-info {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }
                .header-info h3 {
                    margin: 0;
                    font-size: 1.25rem;
                }
                .header-info p {
                    margin: 0;
                    color: #64748b;
                    font-size: 0.875rem;
                }
                .insurers-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 1rem;
                }
                .insurer-card {
                    background: white;
                    border-radius: 12px;
                    padding: 1.25rem;
                    border: 1px solid #e2e8f0;
                    transition: all 0.2s;
                }
                .insurer-card:hover {
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }
                .insurer-card.inactive {
                    opacity: 0.6;
                    background: #f8fafc;
                }
                .insurer-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1rem;
                }
                .insurer-name {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-weight: 600;
                    color: #1e293b;
                }
                .rfc-badge {
                    padding: 0.25rem 0.5rem;
                    background: #e2e8f0;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    color: #475569;
                    font-family: monospace;
                }
                .insurer-details {
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                }
                .detail-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.875rem;
                    color: #64748b;
                }
                .detail-item a {
                    color: #083677;
                    text-decoration: none;
                }
                .insurer-actions {
                    display: flex;
                    gap: 0.5rem;
                    justify-content: flex-end;
                    padding-top: 0.75rem;
                    border-top: 1px solid #e2e8f0;
                }
                .btn-icon {
                    padding: 0.5rem;
                    background: #f1f5f9;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    color: #64748b;
                }
                .btn-icon:hover {
                    background: #e2e8f0;
                    color: #1e293b;
                }
                .btn-icon.toggle {
                    color: #00BBBF;
                }
                .empty-state {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 3rem;
                    color: #64748b;
                }
                .empty-state svg {
                    color: #cbd5e1;
                    margin-bottom: 1rem;
                }
                .empty-state button {
                    margin-top: 1rem;
                    padding: 0.5rem 1rem;
                    background: #083677;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                }
                .insurer-form-modal {
                    max-width: 600px;
                }
            `}</style>
        </div>
    );
};

export default InsurerManager;
